import { useEffect, useState, useCallback } from "react";
import { Tag, Package } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, hasPromo, discountPercent } from "@/lib/format";
import { Loader } from "@/components/common";
import Modal, { TextField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { toast } from "sonner";

export default function AdminPromotions() {
  const [products, setProducts] = useState(null);
  const [edit, setEdit] = useState(null);
  const [tab, setTab] = useState("active");
  const load = useCallback(() => api.get("/products").then((r) => setProducts(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!products) return <Loader />;

  const active = products.filter(hasPromo);
  const list = tab === "active" ? active : products;

  const disable = async (p) => {
    try { await api.put(`/products/${p.id}/promo`, { promo_price: 0, promo_start: "", promo_end: "" }); toast.success("Promotion désactivée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle>Promotions</PageTitle>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("active")} data-testid="promo-tab-active" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "active" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>En promotion ({active.length})</button>
        <button onClick={() => setTab("all")} data-testid="promo-tab-all" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "all" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Tous les produits</button>
      </div>
      <Panel className="divide-y divide-slate-100">
        {list.length === 0 && <p className="p-4 text-sm text-slate-400">Aucune promotion active.</p>}
        {list.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`promo-row-${p.id}`}>
            <div className="w-9 h-9 rounded bg-slate-100 overflow-hidden shrink-0">{(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-2.5 text-slate-300" />}</div>
            <div className="min-w-0"><p className="font-semibold text-slate-800 line-clamp-1">{p.name}</p><p className="text-xs text-slate-400 font-num">{formatFCFA(p.price)}{hasPromo(p) && <span className="text-[#C85A32] font-bold"> → {formatFCFA(p.promo_price)} (−{discountPercent(p)}%)</span>}</p></div>
            {hasPromo(p) && <span className="text-[10px] bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full" data-testid={`promo-active-${p.id}`}>Active{p.promo_end ? ` · fin ${p.promo_end}` : ""}</span>}
            <div className="ml-auto flex gap-2">
              <button onClick={() => setEdit(p)} className="bg-[#0F172A] text-white text-xs font-bold px-3 py-1.5 rounded-lg" data-testid={`promo-edit-${p.id}`}>{hasPromo(p) ? "Modifier" : "Mettre en promo"}</button>
              {hasPromo(p) && <button onClick={() => disable(p)} className="border border-red-200 text-red-600 text-xs font-bold px-3 py-1.5 rounded-lg" data-testid={`promo-disable-${p.id}`}>Désactiver</button>}
            </div>
          </div>
        ))}
      </Panel>
      {edit && <PromoForm product={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function PromoForm({ product, onClose, onDone }) {
  const [f, setF] = useState({ promo_price: product.promo_price || "", promo_start: product.promo_start || "", promo_end: product.promo_end || "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const price = Number(f.promo_price);
    if (!(price > 0 && price < Number(product.price))) return toast.error("Le prix promo doit être inférieur au prix normal.");
    try { await api.put(`/products/${product.id}/promo`, { promo_price: price, promo_start: f.promo_start, promo_end: f.promo_end }); toast.success("Promotion enregistrée"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Promotion — ${product.name}`} onClose={onClose} testid="promo-form">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500">Prix normal : <strong className="font-num">{formatFCFA(product.price)}</strong></p>
        <TextField label="Prix promotionnel (FCFA)" type="number" value={f.promo_price} onChange={set("promo_price")} testid="promo-price" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Début (facultatif)" type="date" value={f.promo_start} onChange={set("promo_start")} testid="promo-start" required={false} />
          <TextField label="Fin de la promo" type="date" value={f.promo_end} onChange={set("promo_end")} testid="promo-end" required={false} />
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2" data-testid="promo-submit"><Tag className="w-4 h-4" /> Activer la promotion</button>
      </form>
    </Modal>
  );
}
